import { invalidateLiveTranscript } from '@/hooks/query/useQueryLiveTranscriptBySessionId';
import { isOnPrem } from '@/utils/buildProfile';
import { scribeHttpV2 } from '@/utils/scribeHTTP';
import { type LedgerRecordingCompletion } from '@heidi/capture/ports';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { finishChunkUploadV3 } from './chunkUploadV3';

export const useFinishChunkRecording = (sessionId: string) => {
  const { mutate } = useMutation<void, void, { recordingId: string }>({
    mutationFn: async ({ recordingId }) => {
      await scribeHttpV2.post<{
        is_success: boolean;
        recording_id: string;
      }>(
        `/sessions/${sessionId}/restful-segment-transcription/${recordingId}:finish`,
        {}
      );
    },
    onSuccess: () => {
      // On-prem: :finish stamps stopped_at and aggregates fragments. Refresh
      // with status=complete once the chunk pipeline is drained.
      if (isOnPrem) {
        void invalidateLiveTranscript(sessionId);
      }
    },
  });

  return useCallback(
    (
      recordingId: string,
      useTranscriptionServiceV2: boolean,
      ledgerCompletion?: LedgerRecordingCompletion
    ) => {
      // v3 recordings finish via their stop marker, not an API call.
      if (ledgerCompletion?.uploadTransport?.kind === 'temporal') {
        void finishChunkUploadV3(
          ledgerCompletion.sessionId,
          recordingId,
          ledgerCompletion.uploadTransport,
          ledgerCompletion.lastChunkIndex,
          ledgerCompletion.gaps
        );
        return;
      }
      // Exhaustiveness: a future transport kind must not silently take the
      // legacy path.
      ledgerCompletion?.uploadTransport satisfies { kind: 'http' } | undefined;
      if (!useTranscriptionServiceV2) mutate({ recordingId });
    },
    [mutate]
  );
};
